import React from "react";
import PropTypes from "prop-types";
import { Box, Divider } from "@mui/material";
import { makeStyles } from "@mui/styles";
import QuestionCard from "./QuestionCard";
import {
  SortableContext,
  verticalListSortingStrategy
} from "@dnd-kit/sortable";
import AnswerView from "../MainContent/AnswerView";
import { useGetTheme } from "../../../../hooks";

const useStyles = makeStyles((theme) => ({
  questionList: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    overflow: "auto"
  },
  instructorQuestions: {
    marginBottom: theme.spacing(2)
  },
  studentQuestions: {
    marginTop: theme.spacing(2)
  },
  divider: {
    backgroundColor: theme.palette.divider,
    margin: theme.spacing(2, 0)
  },
  combinedView: {
    display: "flex",
    flexDirection: "column",
    width: "100%"
  }
}));

const QuestionList = ({
  instructorQuestions = [],
  studentQuestions = [],
  highlights = [],
  answers = [],
  selectedQuestionId,
  onQuestionSelect,
  onAnswerClick,
  onSortQuestions,
  combinedViewMode
}) => {
  const classes = useStyles();
  const theme = useGetTheme();
  // Check if questions exist in both categories
  const hasInstructorQuestions = instructorQuestions.length > 0;
  const hasStudentQuestions = studentQuestions.length > 0;
  const showDivider = hasInstructorQuestions && hasStudentQuestions;

  if (combinedViewMode) {
    // Find the currently selected question
    const selectedQuestion =
      instructorQuestions.find(
        (question) => question.id === selectedQuestionId
      ) ||
      studentQuestions.find((question) => question.id === selectedQuestionId);

    const selectedQuestionIndex = instructorQuestions.findIndex(
      (question) => question.id === selectedQuestionId
    );

    const questionAnswer = answers.find(
      (answer) => answer.interaction_id === selectedQuestionId
    );

    // If no question is selected, show a message or return null
    if (!selectedQuestion) {
      return (
        <Box className={classes.combinedView}>
          <div>No question selected</div>
        </Box>
      );
    }

    return (
      <Box className={classes.combinedView}>
        {/* Display the Question Card */}
        <QuestionCard
          key={selectedQuestionId}
          question={selectedQuestion}
          index={selectedQuestionIndex}
          isSelected={true}
          highlights={highlights}
          answer={questionAnswer}
          onClick={onQuestionSelect}
          onAnswerClick={onAnswerClick}
          combinedViewMode={combinedViewMode}
        />

        {/* Render the AnswerView component below the question */}
        <Box sx={{ paddingInline: theme.spacing(2) }}>
          <AnswerView question={selectedQuestion} width={"inherit"} />
        </Box>
      </Box>
    );
  } else {
    return (
      <Box className={classes.questionList}>
        {/* Instructor Questions */}
        {hasInstructorQuestions && (
          <Box className={classes.instructorQuestions}>
            {instructorQuestions.map((question, index) => {
              const questionAnswer = answers.find(
                (answer) => answer.interaction_id === question.id
              );

              return (
                <QuestionCard
                  key={question.id}
                  question={question}
                  index={index}
                  isSelected={selectedQuestionId === question.id}
                  highlights={highlights}
                  answer={questionAnswer}
                  onClick={onQuestionSelect}
                  onAnswerClick={onAnswerClick}
                />
              );
            })}
          </Box>
        )}

        {/* Divider if both instructor and student questions exist */}
        {showDivider && <Divider className={classes.divider} />}

        {/* Student Questions - Sortable */}
        {hasStudentQuestions && (
          <SortableContext
            items={studentQuestions.map(
              (q) => `sort-student-questions-${q.id}`
            )}
            strategy={verticalListSortingStrategy}>
            <Box className={classes.studentQuestions}>
              {studentQuestions.map((question, index) => {
                const questionAnswer = answers.find(
                  (answer) => answer.interaction_id === question.id
                );

                return (
                  <QuestionCard
                    key={question.id}
                    question={question}
                    index={index + instructorQuestions.length}
                    isSelected={selectedQuestionId === question.id}
                    highlights={highlights}
                    answer={questionAnswer}
                    onClick={onQuestionSelect}
                    onAnswerClick={onAnswerClick}
                    // Additional props for sortable functionality
                    onSortQuestions={onSortQuestions}
                  />
                );
              })}
            </Box>
          </SortableContext>
        )}
      </Box>
    );
  }
};

QuestionList.propTypes = {
  instructorQuestions: PropTypes.array,
  studentQuestions: PropTypes.array,
  highlights: PropTypes.array,
  answers: PropTypes.array,
  selectedQuestionId: PropTypes.string,
  onQuestionSelect: PropTypes.func.isRequired,
  onAnswerClick: PropTypes.func,
  onSortQuestions: PropTypes.func,
  combinedViewMode: PropTypes.bool,
  setCombinedViewMode: PropTypes.func,
  containerWidth: PropTypes.number
};

export default QuestionList;
